// Daten vom Server holen – und was die Seite zeigt, solange sie laden oder
// wenn es nicht geht. Nie ein leerer Bildschirm: „Wird geladen …“ mit
// Kreisel, ein Fehler mit Worten und „Noch einmal“, und eine Abweisung
// wegen der Rolle (403 „keine-berechtigung“) als freundlicher Hinweis mit
// dem Weg zur eigenen Startseite.

import { useCallback, useEffect, useState } from "react";
import { ROLLEN_NAMEN } from "@happy-energy/kern";
import { ApiFehler, holen, Ikon, OhneVerbindung, Tipp } from "@happy-energy/oberflaeche";
import { startpfad } from "./navigation.ts";
import { useCrm } from "./zusammenhang.tsx";

export interface Geladen<T> {
  /** Die Daten – beim Neuladen bleiben die alten stehen, bis die neuen da sind. */
  daten: T | undefined;
  /** Was beim letzten Laden schiefging – sonst undefined. */
  fehler: unknown;
  laedt: boolean;
  /** Noch einmal holen (nach einer Änderung, „Noch einmal“). */
  neu: () => void;
}

interface Ergebnis<T> {
  pfad: string;
  nr: number;
  daten: T | undefined;
  fehler: unknown;
}

/** Holt `pfad` (GET) – neu, sobald sich der Pfad ändert oder `neu()` gerufen wird. Null: nichts holen. */
export function useLaden<T>(pfad: string | null): Geladen<T> {
  const [ergebnis, setErgebnis] = useState<Ergebnis<T> | null>(null);
  const [nr, setNr] = useState(0);
  const neu = useCallback(() => setNr((n) => n + 1), []);

  useEffect(() => {
    if (pfad === null) return;
    let gilt = true;
    holen<T>(pfad).then(
      (daten) => {
        if (gilt) setErgebnis({ pfad, nr, daten, fehler: undefined });
      },
      (fehler: unknown) => {
        // Die alten Daten bleiben stehen – die Seite zeigt den Fehler darüber.
        if (gilt) setErgebnis((alt) => ({ pfad, nr, daten: alt?.pfad === pfad ? alt.daten : undefined, fehler }));
      },
    );
    return () => {
      gilt = false;
    };
  }, [pfad, nr]);

  const passend = ergebnis !== null && ergebnis.pfad === pfad ? ergebnis : null;
  return { daten: passend?.daten, fehler: passend?.fehler, laedt: pfad !== null && (passend === null || passend.nr !== nr), neu };
}

/** Darf die Rolle das nicht? (403 „keine-berechtigung“ vom Server) */
export function keineBerechtigung(fehler: unknown): boolean {
  return fehler instanceof ApiFehler && fehler.grund === "keine-berechtigung";
}

/** Ein Fehler in Worten, für Menschen. */
export function fehlerText(fehler: unknown): string {
  if (fehler instanceof OhneVerbindung) {
    return fehler.zeitueberschreitung ? "Der Server hat nicht rechtzeitig geantwortet – bitte noch einmal." : "Keine Verbindung zum Server – bitte die Verbindung prüfen.";
  }
  if (fehler instanceof ApiFehler) {
    if (fehler.status === 404) return "Das gibt es hier nicht (mehr).";
    if (fehler.status === 401) return "Du bist nicht mehr angemeldet.";
    if (fehler.keinJson || fehler.status >= 500) return `Der Server hat einen Fehler gemeldet (${fehler.status}) – bitte gleich noch einmal.`;
    return fehler.message;
  }
  return fehler instanceof Error ? fehler.message : String(fehler);
}

/** „Wird geladen …“ – mit Kreisel, nie ein leerer Bildschirm. */
export function Laedt({ text = "Wird geladen …" }: { text?: string }) {
  return (
    <div className="crm-laedt" role="status">
      <span className="kreisel" aria-hidden="true" />
      {text}
    </div>
  );
}

/** Der Hinweis „Das gehört nicht zu deiner Rolle“ – mit dem Weg zur eigenen Startseite. */
export function NichtDeineRolle() {
  const { ich } = useCrm();
  return (
    <Tipp art="grau" ikon="schloss">
      <b>Das gehört nicht zu deiner Rolle ({ROLLEN_NAMEN[ich.nutzer.rolle]}).</b> Jede Rolle sieht ihre Sache, die Leitung alles. Was du
      brauchst, findest du links in der Leiste.
      <div className="crm-tipp-knoepfe">
        <a className="crm-knopf rand klein" href={`#${startpfad(ich.nutzer.rolle)}`}>
          Zu deiner Startseite <Ikon name="rechts" groesse="k" />
        </a>
      </div>
    </Tipp>
  );
}

/** Was schiefging – mit „Noch einmal“; bei der Rolle der Hinweis, bei „abgemeldet“ der Weg zur Anmeldung. */
export function LadeFehler({ fehler, nochmal }: { fehler: unknown; nochmal: () => void }) {
  const { hilfen } = useCrm();
  if (keineBerechtigung(fehler)) return <NichtDeineRolle />;
  const abgemeldet = fehler instanceof ApiFehler && fehler.status === 401;
  return (
    <Tipp art="rot" ikon="achtung">
      {fehlerText(fehler)}
      <div className="crm-tipp-knoepfe">
        {abgemeldet ? (
          <button type="button" className="crm-knopf rand klein" onClick={hilfen.neuFragen}>
            Neu anmelden
          </button>
        ) : (
          <button type="button" className="crm-knopf rand klein" onClick={nochmal}>
            Noch einmal
          </button>
        )}
      </div>
    </Tipp>
  );
}
